import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import api from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { ArrowLeft, Eye } from 'lucide-react'
import AffiliateEmailPanel from '@/components/affiliate/AffiliateEmailPanel'
import { useAffiliatePreview } from '@/hooks/useAffiliatePreview'

interface AffiliateMeLite {
  affiliate: { displayName: string }
}

export default function AffiliateEmailPage() {
  const { isPreview, apiParams, linkQuery } = useAffiliatePreview()
  const [previewName, setPreviewName] = useState<string | null>(null)
  const [notFound, setNotFound] = useState(false)

  // Resolve display name when previewing so the slim header tells the admin
  // *which* affiliate they're looking at — easy to forget after clicking
  // through from /admin/affiliates.
  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const res = await api.get<AffiliateMeLite>('/api/affiliates/me', { params: apiParams })
        if (!cancelled) setPreviewName(res.data.affiliate?.displayName || null)
      } catch (err: any) {
        if (!cancelled && err?.response?.status === 404) setNotFound(true)
      }
    }
    load()
    return () => { cancelled = true }
  }, [apiParams])

  if (notFound) {
    return (
      <div className="space-y-4">
        <SlimHeader isPreview={false} previewName={null} linkQuery={linkQuery} />
        <Card className="card-inset">
          <CardContent className="py-10 text-center">
            <p className="text-sm text-muted-foreground">
              This account is not approved as an affiliate yet.
            </p>
            <Button asChild className="mt-4">
              <Link to="/affiliate/apply">Apply to become an affiliate</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <SlimHeader isPreview={isPreview} previewName={previewName} linkQuery={linkQuery} />
      <AffiliateEmailPanel />
    </div>
  )
}

// Slim breadcrumb-style header. Replaces the previous PageHeader that
// duplicated the panel-level title — the breadcrumb already names the
// surface, the panel below carries the working chrome. One identity per
// surface; everything else is noise.
function SlimHeader({
  isPreview,
  previewName,
  linkQuery,
}: {
  isPreview: boolean
  previewName: string | null
  linkQuery: string
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2 text-sm text-muted-foreground min-w-0">
        <Link
          to={`/affiliate${linkQuery}`}
          className="hover:text-foreground transition-colors"
        >
          Affiliate
        </Link>
        <span className="text-muted-foreground/40">›</span>
        <span className="text-foreground font-medium">Email</span>
        {isPreview && previewName && (
          <>
            <span className="text-muted-foreground/40">·</span>
            <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
              <Eye className="h-3 w-3" />
              Previewing {previewName}
            </span>
          </>
        )}
      </div>
      <Button asChild variant="ghost" size="sm" className="h-8 text-muted-foreground hover:text-foreground">
        <Link to={`/affiliate${linkQuery}`}>
          <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
          Back to dashboard
        </Link>
      </Button>
    </div>
  )
}
